<template>
  <div class="bg-white shadow-md rounded-md flex overflow-hidden h-full">
    <div
      class="w-1/2 bg-gray-50 p-3 border-r-[2px] flex flex-col justify-between border-dashed relative overflow-hidden"
    >
      <div class="flex gap-2 items-center z-10 relative">
        <div
          class="w-7 min-w-7 h-7 rounded-md flex justify-center items-center bg-blue-600"
        >
          <img
            src="../../../assets/icon/elektrifikasi-active.png"
            alt="icon"
            loading="eager"
            class="w-4 h-auto"
          />
        </div>
        <h5 class="font-semibold text-[10px] text-gray-800 text-wrap">
          YTD Energy Consume / Ton Coal
        </h5>
      </div>
      <h3 class="text-3xl font-bold text-gray-800 z-10 relative">
        {{ formattedEnergy }}
        <span class="font-normal text-2xl">
          {{ Mj ? "Mj/ Ton" : "Tj/ Ton" }}
        </span>
      </h3>
      <div class="flex justify-between items-start">
        <div class="flex gap-2 items-center relative z-10">
          <div
            class="flex justify-center items-center rounded-md h-7"
            :class="{
              'bg-green-100 pr-2': data?.percentage_change > 0,
              'bg-red-100 pr-2': data?.percentage_change < 0,
              'bg-gray-200 min-w-14': data?.percentage_change == 0,
            }"
          >
            <img
              v-if="data?.percentage_change > 0"
              src="../../../assets/icon/mdi-menu-up.svg"
              alt="icon"
            />
            <img
              v-if="data?.percentage_change < 0"
              src="../../../assets/icon/mdi-menu-down.svg"
              alt="icon"
            />
            <img
              v-if="data?.percentage_change == 0"
              src="../../../assets/icon/mdi-equal.svg"
              alt="icon"
            />
            <h6
              class="font-semibold text-xs"
              v-if="data?.percentage_change && data?.percentage_change !== 0"
              :class="[
                data?.percentage_change >= 0
                  ? 'text-green-600'
                  : 'text-red-600',
              ]"
            >
              {{ data?.percentage_change }}%
            </h6>
          </div>
          <h6 class="text-gray-400 font-normal text-[10px]">
            from<br />
            last month
          </h6>
        </div>
        <div class="form-check z-20 -mr-2 relative">
          <input
            class="form-check-input border-2 border-slate-400"
            type="checkbox"
            v-model="Mj"
            @click="updateUnit()"
          />
          <p
            class="absolute top-6 right-2 text-[8px] text-gray-400 text-nowrap text-right"
          >
            Mega Joule
          </p>
        </div>
      </div>
      <div
        class="w-36 h-36 rounded-full bg-blue-100 -right-14 top-20 -z-0 opacity-50 absolute"
      ></div>
      <div
        class="w-16 h-16 rounded-full bg-blue-100 -right-7 top-12 absolute -z-0 opacity-50"
      ></div>
    </div>
    <div class="w-1/2 flex justify-center items-center">
      <div class="w-32 h-auto font-inter py-3">
        <canvas ref="chartCanvas"> </canvas>
      </div>
    </div>
  </div>
</template>

<script>
import { formatFloat } from "@/utilities/helpers";
import { Chart, registerables } from "chart.js";

export default {
  name: "CardEnergyComponent",
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      chart: null,
      Mj: false,
    };
  },
  watch: {
    data: {
      handler(newData) {
        if (newData) {
          this.initChart();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    Chart.register(...registerables);
    this.initChart();
  },
  computed: {
    formattedEnergy() {
      const energy = this.data?.consume_energy ?? 0;
      const value = this.Mj ? energy * 1000000 : energy;
      return formatFloat(value);
    },
  },
  methods: {
    formatFloat,
    initChart() {
      const ctx = this.$refs.chartCanvas;

      if (!ctx) {
        console.warn("Canvas element not found");
        return;
      }

      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }

      this.chart = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: ["BBM Fosil", "Biofuel", "Elektrifikasi"],
          datasets: [
            {
              data: [
                this.data?.consume_energy_fossil || 0,
                this.data?.consume_energy_biofuel || 0,
                this.data?.electrification || 0,
              ],
              backgroundColor: ["#FF6F6E", "#FDC469", "#6DACA4"],
            },
          ],
        },
        options: {
          responsive: true,
          cutout: "50%",
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              backgroundColor: "#4B5169",
              titleFont: { size: 14, weight: "bold" },
              bodyFont: { size: 10 },
              displayColors: false,
              boxPadding: 8,
              padding: { top: 8, bottom: 8, left: 10, right: 10 },
              callbacks: {
                title(tooltipItems) {
                  const dataset = tooltipItems[0].dataset;
                  const total = dataset.data.reduce((acc, val) => acc + val, 0);
                  const value = dataset.data[tooltipItems[0].dataIndex];
                  const percentage =
                    total === 0 ? 0 : ((value / total) * 100).toFixed(0);
                  return `${percentage}%`;
                },
                label(tooltipItem) {
                  return tooltipItem.label;
                },
              },
            },
          },
        },
        plugins: [
          {
            id: "centerText",
            afterDraw: (chart) => {
              const { width, height } = chart;
              const ctx = chart.ctx;
              if (!ctx) return;
              ctx.save();
              ctx.fillStyle = "#333";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";

              const x = width / 2;
              const y = height / 2;
              ctx.font = "bold 9px inter";
              ctx.fillText("Total Energy", x, y - 6);

              ctx.font = "bold 11px inter";
              ctx.fillText(
                `${formatFloat(this.data?.total_energy) || 0}${
                  this.Mj ? "^6 Mj" : " Tj"
                }`,
                x,
                y + 8
              );
              ctx.restore();
            },
          },
        ],
      });
    },
    updateChartData(newData) {
      if (this.chart) {
        this.chart.data.datasets[0].data = [
          newData.consume_energy_fossil || 0,
          newData.consume_energy_biofuel || 0,
          newData.electrification || 0,
        ];
        this.chart.update();
      }
    },
    updateUnit() {
      this.$nextTick(() => {
        this.initChart();
      });
    },
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
};
</script>
